import { OfferProperties } from '@features/offers/interfaces/offersInterface';
import { OfferBaseFormData } from '@features/products/interfaces/productInterface';

export type RulesTASRelational = {
  fromType: 'acquisition' | 'seasonality' | 'offerType';
  fromId: number;
  toType: 'acquisition' | 'seasonality' | 'offerType';
} & (
  | {
      toInclude: number[];
      toExclude?: never;
    }
  | {
      toExclude: number[];
      toInclude?: never;
    }
);

export const TAS_FIELD_MAP = {
  acquisition: 'acquisitionId',
  seasonality: 'seasonalityId',
  offerType: 'typeId',
} as const;

export type TasRuleType = keyof typeof TAS_FIELD_MAP;
export type TasFieldName = (typeof TAS_FIELD_MAP)[TasRuleType];

export type TasOption = {
  label: string;
  value: string;
};

export type TasOptionsByType = Record<TasRuleType, TasOption[]>;

export type TasRuleState = {
  optionsByType: TasOptionsByType;
  disabledByType: Record<TasRuleType, boolean>;
  fixedValueByType: Partial<Record<TasRuleType, string>>;
};

export type TasDependentFieldUpdate = {
  fieldName: TasFieldName;
  value: string;
};

function sortTasOptionsById(options: TasOption[]): TasOption[] {
  return [...options].sort((current, next) => {
    return Number(current.value) - Number(next.value);
  });
}

export function getTasFieldName(type: TasRuleType): TasFieldName {
  return TAS_FIELD_MAP[type];
}

export function buildTasOptionsByType(
  offerProperties?: OfferProperties
): TasOptionsByType {
  return {
    offerType: sortTasOptionsById(
      offerProperties?.types?.map(type => ({
        label: type.name,
        value: String(type.id),
      })) || []
    ),
    acquisition: sortTasOptionsById(
      offerProperties?.acquisitionTypes?.map(type => ({
        label: type.name,
        value: String(type.id),
      })) || []
    ),
    seasonality: sortTasOptionsById(
      offerProperties?.seasonalities?.map(seasonality => ({
        label: seasonality.name,
        value: String(seasonality.id),
      })) || []
    ),
  };
}

export function getTasRuleState(
  offer: OfferBaseFormData,
  baseOptionsByType: TasOptionsByType,
  isReadOnly: boolean,
  rules: RulesTASRelational[]
): TasRuleState {
  const optionsByType = {
    offerType: baseOptionsByType.offerType,
    acquisition: baseOptionsByType.acquisition,
    seasonality: baseOptionsByType.seasonality,
  };

  const disabledByType = {
    offerType: false,
    acquisition: false,
    seasonality: false,
  };

  const fixedValueByType: Partial<Record<TasRuleType, string>> = {};

  if (isReadOnly) {
    return {
      optionsByType,
      disabledByType,
      fixedValueByType,
    };
  }

  const excludedValuesByType: Record<TasRuleType, Set<string>> = {
    offerType: new Set(),
    acquisition: new Set(),
    seasonality: new Set(),
  };
  const includedValuesByType: Record<TasRuleType, Set<string> | null> = {
    offerType: null,
    acquisition: null,
    seasonality: null,
  };

  rules.forEach(rule => {
    const fromField = TAS_FIELD_MAP[rule.fromType];
    const currentValue = Number(offer?.[fromField]);

    if (!currentValue || currentValue !== rule.fromId) return;

    const targetOptions = baseOptionsByType[rule.toType];

    if ('toInclude' in rule) {
      const validIncludedValues = (rule.toInclude || [])
        .filter(includedId =>
          targetOptions.some(option => Number(option.value) === includedId)
        )
        .map(String);

      if (validIncludedValues.length === 0) return;

      includedValuesByType[rule.toType] = new Set(validIncludedValues);

      if (validIncludedValues.length === 1 && !fixedValueByType[rule.toType]) {
        fixedValueByType[rule.toType] = validIncludedValues[0];
        disabledByType[rule.toType] = true;
      }

      return;
    }

    (rule.toExclude || []).forEach(excludedId => {
      const excludedOptionExists = targetOptions.some(
        option => Number(option.value) === excludedId
      );

      if (excludedOptionExists) {
        excludedValuesByType[rule.toType].add(String(excludedId));
      }
    });
  });

  const resolvedOptionsByType = (
    Object.keys(optionsByType) as TasRuleType[]
  ).reduce<TasOptionsByType>((accumulator, type) => {
    const fixedValue = fixedValueByType[type];
    const includedValues = includedValuesByType[type];

    if (fixedValue) {
      accumulator[type] = optionsByType[type].filter(
        option => option.value === fixedValue
      );
      return accumulator;
    }

    accumulator[type] = optionsByType[type].filter(
      option =>
        !excludedValuesByType[type].has(option.value) &&
        (!includedValues || includedValues.has(option.value))
    );
    return accumulator;
  }, optionsByType);

  return {
    optionsByType: resolvedOptionsByType,
    disabledByType,
    fixedValueByType,
  };
}

export function getTasDependentFieldUpdates(
  offer: OfferBaseFormData,
  ruleState: TasRuleState
): TasDependentFieldUpdate[] {
  return (Object.entries(TAS_FIELD_MAP) as [TasRuleType, TasFieldName][])
    .map(([type, fieldName]) => {
      const currentValue = offer[fieldName];
      const fixedValue = ruleState.fixedValueByType[type];
      const availableOptions = ruleState.optionsByType[type];
      const hasCurrentValue = !!currentValue;
      const hasAvailableOptions = availableOptions.length > 0;
      const isCurrentValueAllowed = availableOptions.some(
        option => option.value === currentValue
      );

      if (fixedValue && currentValue !== fixedValue) {
        return {
          fieldName,
          value: fixedValue,
        };
      }

      if (
        !fixedValue &&
        hasCurrentValue &&
        hasAvailableOptions &&
        !isCurrentValueAllowed
      ) {
        return {
          fieldName,
          value: '',
        };
      }

      return null;
    })
    .filter((update): update is TasDependentFieldUpdate => update !== null);
}
